import React, { useEffect } from 'react';
import { Box, Alert } from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useAppDispatch, useAppSelector } from 'store/store';
import { createDrawing } from 'store/actions/drawingActions';
import { fetchDrawingBases } from 'store/actions/drawingBaseActions';
import { CustomSelect } from 'components/FormFields/CustomSelect/CustomSelect';
import { CustomDatePicker } from 'components/FormFields/CustomDatePicker/CustomDatePicker';
import RedButton from 'Styled/RedButton';
import TextButton from 'Styled/TextButton';
import { isFulfilled } from '@reduxjs/toolkit';
import { PageHeader } from 'components/Layout/PageHeader';
import { isDrawingBaseComplete } from 'utility/drawingBaseCompleteness';
import { preventEnterSubmit } from 'utility/preventEnterSubmit';

const validationSchema = Yup.object({
  drawingBaseId: Yup.number().required('Ziehungsbasis ist erforderlich'),
  drawingDate: Yup.string().required('Ziehungsdatum ist erforderlich'),
});

export const DrawingCreatePage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { lotteryId } = useParams<{ lotteryId: string }>();
  const [searchParams] = useSearchParams();
  const { drawingBases } = useAppSelector((state) => state.drawingBases);

  useEffect(() => {
    if (lotteryId) {
      dispatch(fetchDrawingBases(Number(lotteryId)));
    }
  }, [dispatch, lotteryId]);

  const usableBases = drawingBases.filter(isDrawingBaseComplete);
  const hiddenCount = drawingBases.length - usableBases.length;
  const drawingBaseOptions = usableBases.map((db) => ({ id: db.id, title: db.name }));

  return (
    <Box>
      <PageHeader title="Neue Ziehung" />

      {hiddenCount > 0 && (
        <Alert severity="info" sx={{ marginBottom: 2 }}>
          {hiddenCount === 1 ? 'Eine Ziehungsbasis steht' : `${hiddenCount} Ziehungsbasen stehen`} nicht zur Auswahl: Sachgewinne brauchen Titel und Beschreibung.
        </Alert>
      )}

      <Formik
        initialValues={{
          drawingBaseId: searchParams.get('drawingBaseId') ?? '',
          drawingDate: '',
          drawingDateNext: '',
          datePublish: '',
        }}
        validationSchema={validationSchema}
        onSubmit={async (values) => {
          const result = await dispatch(
            createDrawing({
              drawingBaseId: Number(values.drawingBaseId),
              drawingDate: values.drawingDate || undefined,
              drawingDateNext: values.drawingDateNext || undefined,
              datePublish: values.datePublish || undefined,
            })
          );
          if (isFulfilled(result)) {
            navigate(`/lottery/${lotteryId}/drawings/${result.payload.id}/edit`);
          }
        }}
      >
        {({ values, errors, touched, setFieldValue, isSubmitting }) => (
          <Form onKeyDown={preventEnterSubmit}>
            <Box sx={{ maxWidth: 600, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <CustomSelect name="drawingBaseId" label="Ziehungsbasis" options={drawingBaseOptions} value={values.drawingBaseId} onChange={(val) => setFieldValue('drawingBaseId', val)} error={touched.drawingBaseId && Boolean(errors.drawingBaseId)} helperText={touched.drawingBaseId ? (errors.drawingBaseId as string) : ''} />

              <CustomDatePicker label="Datum der aktuellen Ziehung" value={values.drawingDate} onChange={(val) => setFieldValue('drawingDate', val)} error={touched.drawingDate && Boolean(errors.drawingDate)} helperText={touched.drawingDate ? (errors.drawingDate as string) : ''} />

              <CustomDatePicker label="Datum der nächsten Ziehung" value={values.drawingDateNext} onChange={(val) => setFieldValue('drawingDateNext', val)} />

              <CustomDatePicker label="Veröffentlichung der nächsten Ziehung" value={values.datePublish} onChange={(val) => setFieldValue('datePublish', val)} />

              <Box sx={{ display: 'flex', gap: 2, marginTop: 2 }}>
                <RedButton type="submit" disabled={isSubmitting}>
                  Erstellen
                </RedButton>
                <TextButton onClick={() => navigate(`/lottery/${lotteryId}/drawings`)}>Abbrechen</TextButton>
              </Box>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
};
